import {
  TUNING_HISTORY_STATUS,
  tuningHistoryFromFirestore,
} from '@parallel-fluidics/shared/models/tuningHistory';
import {
  createTuningHistory,
  getLatestTuningHistoryQuery,
  updateTuningHistory,
} from 'features/tuningHistory/api';
import { Timestamp } from 'firebase/firestore';
import useRealtimeDocs from 'hooks/use-realtimeDocs';
import {
  useCallback,
  useMemo,
} from 'react';

const averageSide = (entries, side) => {
  if (entries.length === 0) {
    return null;
  }

  const totals = entries.reduce((accumulator, entry) => ({
    kp: accumulator.kp + entry[side].kp,
    tnSeconds: accumulator.tnSeconds + entry[side].tnSeconds,
    tvSeconds: accumulator.tvSeconds + entry[side].tvSeconds,
    tdSeconds: accumulator.tdSeconds + entry[side].tdSeconds,
  }), {
    kp: 0,
    tnSeconds: 0,
    tvSeconds: 0,
    tdSeconds: 0,
  });

  return {
    kp: totals.kp / entries.length,
    tnSeconds: totals.tnSeconds / entries.length,
    tvSeconds: totals.tvSeconds / entries.length,
    tdSeconds: totals.tdSeconds / entries.length,
  };
};

function usePidHistory(pressId) {
  const historyQuery = useMemo(() => getLatestTuningHistoryQuery(pressId), [pressId]);
  const { data: rawHistory = [], status } = useRealtimeDocs({
    query: historyQuery,
    transformData: tuningHistoryFromFirestore,
  });
  const history = useMemo(
    () => rawHistory.filter((entry) => entry.validation !== 'failed'),
    [rawHistory],
  );

  const createSession = useCallback(async ({
    isFullSession = false,
    upper = null,
    lower = null,
  } = {}) => {
    if (!pressId) {
      return null;
    }
    const tuningHistoryData = {
      pressId,
      startTime: Timestamp.now(),
      status: TUNING_HISTORY_STATUS.IN_PROGRESS,
    };
    if (isFullSession) {
      tuningHistoryData.upper = upper;
      tuningHistoryData.lower = lower;
      tuningHistoryData.status = TUNING_HISTORY_STATUS.COMPLETE;
      tuningHistoryData.endTime = Timestamp.now();
      // Set start time to 10 minutes ago to ensure it appears in the history list immediately
      tuningHistoryData.startTime = Timestamp.fromDate(new Date(Date.now() - 10 * 60 * 1000));
    }
    const sessionRef = await createTuningHistory(tuningHistoryData);
    return {
      id: sessionRef.id,
      ...tuningHistoryData,
    };
  }, [pressId]);

  const completeSession = useCallback(async ({
    session,
    upper,
    lower,
    isAbandon = false,
  }) => {
    if (!pressId || !session) {
      return null;
    }

    await updateTuningHistory(session.id, {
      ...session,
      endTime: Timestamp.now(),
      status: isAbandon ? TUNING_HISTORY_STATUS.ABANDONED : TUNING_HISTORY_STATUS.COMPLETE,
      upper,
      lower,
    });

    return true;
  }, [pressId]);

  const averages = useMemo(() => ({
    upper: averageSide(history, 'upper'),
    lower: averageSide(history, 'lower'),
  }), [history]);

  return {
    history,
    status,
    averages,
    createSession,
    completeSession,
  };
}

export default usePidHistory;
